// FILE: src/components/AdminScreen.js
//
// Admin landing page — "Mission Control".
//
// Operational dashboard that replaced the launcher grid. Renders a live
// engagement banner, a unified health console (Platform Health, Learner
// Readiness, Certification Mix, active-user trends), and downstream sections.
// Navigation to every existing admin tool is preserved via the Quick Actions
// ribbon — all admin-open-* testids + onOpen* props unchanged (App.js untouched).
//
// Data: real values are loaded from existing services with graceful fallback to
// placeholder samples (so the dashboard always renders even if a read fails).
//   • Question-bank counts + cert usage mix  -> dashboardService.loadDashboardData
//   • Active-today / monthly-active + trends  -> telemetryService
//   • Reported-questions awaiting review      -> dashboard review status
//   • Operational queue (all five rows)       -> buildQueueRows():
//       reported count + oldest age            dashboardService
//       pending adjudication (+ per cert)      question_factory_dashboard RPC (mig 082)
//       coverage below target (<60% areas)     coverageService matrix
//       latest QF run                          list_pipeline_runs RPC
//       telemetry health                       telemetry read success
//   • Platform health / alerts (30d)          -> telemetryService.fetchTechnicalHealth
//     (score + alert line derived in loadData; matches the Telemetry screen's
//      default 30-day window so the dial agrees with the drill-down)
//   • Learner readiness + distribution +      -> telemetryService.fetchLearnerHealth
//     domain signals + low-success /             (admin_learner_health RPC, mig 084)
//     never-served insights
//   • QF throughput pipeline + chips          -> question_factory_dashboard RPC +
//                                                today's/7d pipeline-run sums
//   • Recent activity feed                    -> question_history (dashboard),
//                                                pipeline runs, releases.json
// Every number on this screen is now real (or shows an explicit loading /
// unavailable state) — nothing is hardcoded sample data.
//
// The visual canvas is rendered via dangerouslySetInnerHTML (built from the data
// object) and SVG instruments are drawn in a useEffect. Interactive controls
// (Back, Sign out, Quick Actions) are real React elements.

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AdminGateContext } from './AdminGate';
import AdminQuickActions, { QUICK_ACTION_TOOLS } from './AdminQuickActions';
import { loadDashboardData, CHANGE_TYPE_META } from '../../services/dashboardService';
import { fetchActiveUsers, fetchDailyActive, toDailyActiveSeries, fetchActiveLearners, fetchRegisteredUsersCount, fetchExamReadyLearners, fetchCertifiedLearners, fetchTechnicalHealth, fetchLearnerHealth } from '../../services/telemetryService';
import { getActiveCertIds, getExamConfig } from '../../config/exams';
import { computeCoverageMatrix, bandVar, formatEta } from '../../services/coverageService';
import { getDashboard, listPipelineRuns, aggregateThroughput } from '../../services/questionFactoryService';
import releases from '../../data/releases.json';

const CSS = `
  :root {
    --header-bg:#14142a; --bridge-bg:#1a1a2e; --page-bg:#f4f5f8; --card-bg:#fff; --border:#e6e6ea;
    --text:#1d1d29; --muted:#66667a; --light:#9a9aae; --accent:#1565c0; --accent-soft:#e8f0fb;
    --green:#2e9e4f; --green-soft:#e7f6ec; --orange:#e8881a; --orange-soft:#fdf1e0; --red:#d83a3a; --red-soft:#fce8e8;
    --yellow:#e9b40a; --purple:#7a3bbf; --teal:#1597a4;
    --shadow:0 1px 3px rgba(20,20,40,0.06), 0 6px 18px rgba(20,20,40,0.05);
    --shadow-hover:0 2px 8px rgba(20,20,40,0.10), 0 12px 28px rgba(20,20,40,0.09);
  }
  * { box-sizing:border-box; }
  body { margin:0; font-family:'Segoe UI', system-ui, -apple-system, sans-serif; background:var(--page-bg); color:var(--text); -webkit-font-smoothing:antialiased; padding-bottom:74px; }

  .mc-top { background:var(--header-bg); color:#fff; padding:11px 26px; display:flex; align-items:center; gap:14px; position:sticky; top:0; z-index:60; }
  .mc-top .back,.mc-top .signout { padding:5px 13px; background:transparent; border:1px solid #44445e; border-radius:6px; color:#bcbcd0; cursor:pointer; font-size:0.8rem; }
  .mc-top .brand { font-size:1.02rem; font-weight:700; letter-spacing:0.5px; }
  .mc-top .sp { flex:1; } .mc-top .meta { font-size:0.72rem; color:#7a7a96; } .mc-top .user { font-size:0.78rem; color:#9a9ab4; }

  .mc-body { max-width:1340px; margin:0 auto; padding:22px 26px 40px; }

  /* ===== UNIFIED CONSOLE (three columns) ===== */
  .console { background: radial-gradient(120% 150% at 14% 0%, #232347 0%, #16162c 58%, #111124 100%); border-radius:18px; padding:14px; box-shadow:0 10px 34px rgba(16,16,40,0.28); position:relative; overflow:hidden; }
  .console::after { content:""; position:absolute; inset:0; background-image:radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px); background-size:22px 22px; pointer-events:none; }
  .console-table { position:relative; z-index:1; display:grid; grid-template-columns:300px 1.15fr 1fr; gap:12px; align-items:stretch; }
  @media (max-width:980px){ .console-table{ grid-template-columns:1fr 1fr; } .health-tile{ grid-column:1 / -1; } }
  @media (max-width:560px){ .console-table{ grid-template-columns:1fr; } }
  .col-stack { display:flex; flex-direction:column; gap:12px; } .col-stack > .tile { flex:1; }

  .tile { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.09); border-radius:13px; padding:14px; color:#fff; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; position:relative; }
  .tile.click { cursor:pointer; transition:transform .14s, background .14s, border-color .14s, box-shadow .14s; }
  .tile.click:hover { background:rgba(255,255,255,0.10); border-color:rgba(120,170,255,0.6); transform:translateY(-2px); box-shadow:0 8px 22px rgba(0,0,0,0.25); }
  .tile.click:active { transform:translateY(0) scale(0.99); }
  .tile-cap { position:absolute; top:10px; left:13px; font-size:0.62rem; letter-spacing:1.3px; text-transform:uppercase; color:#9a9ac0; font-weight:800; }
  .open-ind { position:absolute; top:9px; right:12px; font-size:0.8rem; color:#aab; opacity:0; transition:opacity .14s; }
  .tile.click:hover .open-ind { opacity:1; }

  .health-tile { grid-column:1; gap:12px; padding:20px 16px; justify-content:flex-start; }
  .con-title { font-size:0.64rem; letter-spacing:2.5px; color:#9a9ac0; text-transform:uppercase; font-weight:800; }
  .health-wrap { position:relative; width:186px; height:186px; }
  .health-center { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; }
  .health-center .hc-v { font-size:2.9rem; font-weight:800; line-height:1; letter-spacing:-1px; }
  .health-center .hc-of { font-size:0.82rem; color:#aab; font-weight:600; }
  .health-center .hc-l { font-size:0.58rem; letter-spacing:2px; color:#8a8aa6; text-transform:uppercase; margin-top:5px; }
  .status { display:inline-flex; align-items:center; gap:9px; padding:7px 16px; border-radius:999px; font-size:0.96rem; font-weight:800; letter-spacing:0.5px; background:rgba(46,158,79,0.16); border:1px solid rgba(46,158,79,0.5); color:#7ee59a; }
  .status .led { width:10px; height:10px; border-radius:50%; background:#43d160; box-shadow:0 0 0 4px rgba(67,209,96,0.22); animation:pulse 2.4s infinite; }
  .status.warn { background:rgba(232,136,26,0.16); border-color:rgba(232,136,26,0.5); color:#ffc37a; }
  .status.warn .led { background:#e8881a; box-shadow:0 0 0 4px rgba(232,136,26,0.22); }
  .status.bad { background:rgba(216,58,58,0.16); border-color:rgba(216,58,58,0.5); color:#ff9a9a; }
  .status.bad .led { background:#ff5b5b; box-shadow:0 0 0 4px rgba(216,58,58,0.25); }
  .status.unknown { background:rgba(255,255,255,0.07); border-color:rgba(255,255,255,0.22); color:#c8c8e0; }
  .status.unknown .led { background:#9a9ac0; box-shadow:0 0 0 4px rgba(255,255,255,0.10); animation:none; }
  @keyframes pulse { 0%,100%{box-shadow:0 0 0 4px rgba(67,209,96,0.22);} 50%{box-shadow:0 0 0 7px rgba(67,209,96,0.05);} }
  .alert-line { font-size:0.74rem; color:#ffd166; font-weight:700; }
  .alert-line.ok { color:#7ee59a; }
  .coach { display:flex; align-items:flex-end; gap:8px; margin-top:2px; }
  .coach .bubble { background:#fff; color:#27273a; border-radius:13px; padding:9px 12px; font-size:0.78rem; line-height:1.4; text-align:left; box-shadow:0 6px 18px rgba(0,0,0,0.18); }
  .coach .bubble b { color:var(--accent); } .coach svg { flex-shrink:0; }

  .instr-body { display:flex; align-items:center; gap:18px; width:100%; justify-content:center; padding:8px 4px; }
  .gauge-md { position:relative; width:132px; height:132px; flex-shrink:0; }
  .gauge-md-center { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; }
  .gauge-md-center .gv { font-size:2rem; font-weight:800; line-height:1; }
  .gauge-md-center .gl { font-size:0.56rem; letter-spacing:1.2px; text-transform:uppercase; color:#9a9ac0; margin-top:4px; }
  .ctx { font-size:0.82rem; line-height:1.5; color:#c8c8e0; text-align:left; max-width:230px; } .ctx b { color:#fff; }
  .c-legend { display:flex; flex-direction:column; gap:8px; font-size:0.82rem; color:#c8c8e0; text-align:left; min-width:150px; }
  .c-legend span { display:flex; align-items:center; gap:8px; } .c-legend i { width:10px; height:10px; border-radius:3px; flex-shrink:0; }
  .c-legend b { color:#fff; margin-left:auto; padding-left:14px; font-variant-numeric:tabular-nums; }

  /* console live-now tile */
  .live-tile { justify-content:center; gap:7px; }
  .live-ring { position:relative; width:104px; height:104px; }
  .live-ring .lr-ico { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#43d160; }
  .live-num { font-size:2.4rem; font-weight:800; color:#43d160; line-height:1; }
  .live-lab { font-size:0.68rem; letter-spacing:1.6px; font-weight:800; color:#cfe9d6; text-transform:uppercase; }
  .live-sub { font-size:0.72rem; color:#9a9ac0; } .live-upd { font-size:0.66rem; color:#7a7a96; margin-top:3px; }

  /* sections */
  .sec { margin-top:26px; }
  .sec-head { display:flex; align-items:baseline; gap:10px; margin:0 0 13px; }
  .sec-num { font-size:0.7rem; font-weight:800; color:#fff; background:var(--accent); width:20px; height:20px; border-radius:6px; display:flex; align-items:center; justify-content:center; }
  .sec-title { font-size:0.82rem; font-weight:800; color:var(--muted); text-transform:uppercase; letter-spacing:1.4px; margin:0; }
  .sec-sub { font-size:0.78rem; color:var(--light); }
  .card { background:var(--card-bg); border:1px solid var(--border); border-radius:14px; box-shadow:var(--shadow); }
  .grid-2 { display:grid; grid-template-columns:1.35fr 1fr; gap:18px; }
  @media (max-width:1040px){ .grid-2{grid-template-columns:1fr;} }
  .card-h { padding:15px 18px 2px; } .card-h h3 { margin:0; font-size:0.98rem; font-weight:800; } .card-h p { margin:3px 0 0; font-size:0.77rem; color:var(--light); }

  /* ===== PLATFORM PULSE ===== */
  .pulse { background:linear-gradient(160deg,#1b2235 0%, #141a2b 58%, #0f1422 100%); border:1px solid #283250; border-radius:18px; padding:22px 24px; color:#e7ecf5; box-shadow:0 10px 30px rgba(10,14,28,0.25); }
  .pulse-head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:1px solid rgba(255,255,255,0.08); padding-bottom:16px; }
  .pulse-title { display:flex; align-items:center; gap:14px; }
  .pulse-ico { width:46px; height:46px; border-radius:12px; background:rgba(67,209,96,0.12); border:1px solid rgba(67,209,96,0.35); display:flex; align-items:center; justify-content:center; color:#43d160; }
  .pulse-title h2 { margin:0; font-size:1.45rem; font-weight:800; letter-spacing:1.5px; }
  .pulse-title p { margin:3px 0 0; font-size:0.82rem; color:#8b93a7; }
  .pulse-status { text-align:right; }
  .live-pill { display:inline-flex; align-items:center; gap:7px; font-size:0.74rem; font-weight:800; letter-spacing:0.6px; color:#5fe08a; background:rgba(46,158,79,0.14); border:1px solid rgba(46,158,79,0.4); padding:5px 13px; border-radius:999px; }
  .live-pill .ld { width:7px; height:7px; border-radius:50%; background:#43d160; box-shadow:0 0 0 3px rgba(67,209,96,0.25); animation:pulse 2s infinite; }
  .pulse-upd { display:flex; align-items:center; gap:7px; justify-content:flex-end; margin-top:9px; font-size:0.78rem; color:#8b93a7; }

  .pulse-hero { display:flex; align-items:center; justify-content:center; gap:22px; padding:22px 0 24px; }
  .hero-ring { position:relative; width:96px; height:96px; }
  .hero-ring .lr-ico { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#cfe9d6; }
  .hero-num { font-size:3.4rem; font-weight:800; color:#43d160; line-height:1; }
  .hero-txt .hero-lab { font-size:1rem; font-weight:800; letter-spacing:1.2px; color:#cfe9d6; margin-left:4px; }
  .hero-txt .hero-sub { font-size:0.88rem; color:#8b93a7; margin-top:5px; }
  .hero-row { display:flex; align-items:baseline; gap:14px; }

  .pulse-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  @media (max-width:820px){ .pulse-grid{ grid-template-columns:1fr; } }
  .pmetric { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.08); border-radius:14px; padding:16px 18px; }
  .pm-top { display:flex; align-items:center; gap:12px; }
  .pm-ico { width:34px; height:34px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; font-size:1rem; flex-shrink:0; }
  .pm-title { font-size:0.82rem; font-weight:800; letter-spacing:0.6px; color:#cdd4e3; text-transform:uppercase; flex:1; }
  .grow-pill { font-size:0.68rem; font-weight:800; color:#5fe08a; background:rgba(46,158,79,0.14); border:1px solid rgba(46,158,79,0.35); padding:3px 10px; border-radius:999px; white-space:nowrap; }
  .pm-val { font-size:2.5rem; font-weight:800; line-height:1; margin:13px 0 3px; display:flex; align-items:baseline; gap:10px; }
  .pm-val .tr { font-size:1rem; font-weight:800; color:#5fe08a; }
  .pm-vs { font-size:0.8rem; color:#8b93a7; }
  .pm-chart { margin-top:14px; display:flex; gap:8px; }
  .pm-yax { display:flex; flex-direction:column; justify-content:space-between; font-size:0.62rem; color:#717a90; text-align:right; padding:2px 0 16px; min-width:30px; }
  .pm-plot { flex:1; }
  .pm-plot svg { width:100%; height:96px; display:block; }
  .pm-axis { display:flex; justify-content:space-between; font-size:0.64rem; color:#717a90; margin-top:5px; }

  .pulse-insight { display:flex; align-items:center; gap:18px; margin-top:18px; padding:14px 18px; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.08); border-radius:14px; }
  .pi-mascot { flex-shrink:0; }
  .pi-body { flex:1; }
  .pi-k { font-size:0.74rem; font-weight:800; letter-spacing:1.2px; color:#4a9eff; }
  .pi-h { font-size:1.08rem; font-weight:800; margin:3px 0 5px; color:#fff; }
  .pi-t { font-size:0.86rem; color:#9aa3b6; line-height:1.45; } .pi-t b { color:#5fe08a; }
  .pi-right { display:flex; align-items:center; gap:13px; border-left:1px solid rgba(255,255,255,0.1); padding-left:18px; }
  .pi-right .pir-ico { color:#4a9eff; } .pir-num .pir-v { font-size:1.7rem; font-weight:800; color:#4a9eff; line-height:1; } .pir-num .pir-l { font-size:0.72rem; color:#8b93a7; margin-top:2px; }
  @media (max-width:680px){ .pulse-insight{ flex-wrap:wrap; } .pi-right{ border-left:none; padding-left:0; } }

  /* ===== PLATFORM PULSE BANNER ===== */
  .pulse-banner{ background:linear-gradient(120deg,#1b2235 0%, #16203a 55%, #0f1626 100%); border:1px solid #283250; border-radius:16px; padding:14px 22px; display:flex; align-items:center; gap:22px; color:#e7ecf5; box-shadow:0 8px 24px rgba(10,14,28,0.22); flex-wrap:wrap; margin-bottom:14px; }
  .pb-brand{ display:flex; align-items:center; gap:13px; }
  .pb-title{ font-size:1.18rem; font-weight:800; letter-spacing:1.3px; }
  .pb-sub{ font-size:0.76rem; color:#8b93a7; margin-top:2px; }
  .pb-live{ display:flex; align-items:center; gap:14px; padding-left:22px; border-left:1px solid rgba(255,255,255,0.08); }
  .pb-live .hero-ring{ width:62px; height:62px; }
  .pb-num{ font-size:1.95rem; font-weight:800; color:#43d160; line-height:1; }
  .pb-numlab{ font-size:0.8rem; font-weight:800; letter-spacing:1px; color:#cfe9d6; margin-left:4px; }
  .pb-status{ margin-left:auto; text-align:right; }
  .pb-upd{ font-size:0.74rem; color:#8b93a7; margin-top:8px; }
  @media (max-width:760px){ .pb-status{ margin-left:0; } .pb-live{ border-left:none; padding-left:0; } }

  /* console metric cards (relocated pulse charts, formatted like Platform Pulse) */
  .console-table{ align-items:stretch; }
  .metric-card{ align-items:stretch; justify-content:center; text-align:left; padding:14px 15px; }
  .mc2-top{ display:flex; align-items:center; gap:10px; }
  .mc2-ico{ width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; flex-shrink:0; }
  .mc2-title{ font-size:0.66rem; letter-spacing:0.7px; text-transform:uppercase; color:#cdd4e3; font-weight:800; flex:1; }
  .mc2-grow{ font-size:0.6rem; font-weight:800; color:#5fe08a; background:rgba(46,158,79,0.14); border:1px solid rgba(46,158,79,0.35); padding:2px 8px; border-radius:999px; white-space:nowrap; }
  .mc2-grow.down{ color:#ff9a9a; background:rgba(216,58,58,0.14); border-color:rgba(216,58,58,0.35); }
  .mc2-grow.flat{ color:#c8c8e0; background:rgba(255,255,255,0.08); border-color:rgba(255,255,255,0.22); }
  .mc2-val{ font-size:1.7rem; font-weight:800; line-height:1; margin:10px 0 3px; display:flex; align-items:baseline; gap:8px; }
  .mc2-val .tr{ font-size:0.8rem; font-weight:800; color:#5fe08a; }
  .mc2-vs{ font-size:0.72rem; color:#8b93a7; }
  .mc2-chart{ display:flex; gap:7px; margin-top:10px; }
  .mc2-yax{ display:flex; flex-direction:column; justify-content:space-between; font-size:0.56rem; color:#7a8499; text-align:right; min-width:26px; padding:2px 0 3px; }
  .mc2-plot{ flex:1; }
  .mc2-plot svg{ width:100%; height:60px; display:block; }
  .mc2-axis{ display:flex; justify-content:space-between; font-size:0.58rem; color:#7a8499; margin-top:5px; padding-left:33px; }


  /* Operational queue */
  .queue { padding:6px 6px 8px; }
  .q-row { display:flex; align-items:center; gap:14px; padding:13px 16px; margin:0 8px; border-top:1px solid var(--border); }
  .q-row:first-child { border-top:none; }
  .q-led { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
  .led-red{background:var(--red);box-shadow:0 0 0 4px var(--red-soft);} .led-orange{background:var(--orange);box-shadow:0 0 0 4px var(--orange-soft);}
  .led-yellow{background:var(--yellow);box-shadow:0 0 0 4px #fbf3d0;} .led-green{background:var(--green);box-shadow:0 0 0 4px var(--green-soft);}
  .led-gray{background:#c3c3d1;box-shadow:0 0 0 4px #eef0f4;}
  .q-text{flex:1;font-size:0.92rem;} .q-text b{font-weight:800;} .q-meta{font-size:0.76rem;color:var(--light);}
  .q-act{padding:6px 15px;border-radius:8px;border:none;font-size:0.8rem;font-weight:700;cursor:pointer;color:#fff;}
  .act-red{background:var(--red);} .act-orange{background:var(--orange);} .act-blue{background:var(--accent);} .act-ghost{background:#eef0f4;color:var(--muted);}
  .q-prio{font-size:0.66rem;font-weight:800;letter-spacing:0.5px;color:var(--light);width:30px;text-align:center;}

  /* pipeline */
  .pipe { display:flex; align-items:stretch; padding:20px 18px 8px; flex-wrap:wrap; }
  .pipe-stage { flex:1; min-width:120px; text-align:center; position:relative; }
  .pipe-node { background:#fafbfd; border:2px solid var(--border); border-radius:12px; padding:14px 8px; margin:0 6px; }
  .pipe-node .pn-v { font-size:1.8rem; font-weight:800; line-height:1; } .pipe-node .pn-l { font-size:0.76rem; color:var(--muted); font-weight:700; margin-top:5px; } .pipe-node .pn-drop { font-size:0.68rem; color:var(--light); margin-top:3px; }
  .pipe-arrow { position:absolute; right:-9px; top:50%; transform:translateY(-50%); font-size:1.1rem; color:var(--light); z-index:2; }
  .pipe-metrics { display:grid; grid-template-columns:repeat(5,1fr); gap:10px; padding:8px 18px 18px; }
  .pm-chip { background:#fafbfd; border:1px solid var(--border); border-radius:10px; padding:9px 10px; text-align:center; }
  .pm-chip .v{font-size:1.05rem;font-weight:800;} .pm-chip .l{font-size:0.68rem;color:var(--muted);margin-top:2px;}
  @media(max-width:760px){ .pipe-metrics{grid-template-columns:repeat(2,1fr);} }

  /* readiness distribution + insights */
  .rd-dist { padding:18px; } .rd-bar-row { display:flex; align-items:center; gap:10px; margin-bottom:9px; }
  .rd-lab{width:72px;font-size:0.78rem;color:var(--muted);} .rd-track{flex:1;background:#eef0f4;border-radius:6px;height:14px;overflow:hidden;}
  .rd-fill{height:100%;border-radius:6px;} .rd-n{width:90px;text-align:right;font-size:0.78rem;font-weight:700;}
  .insight { display:flex; gap:10px; align-items:flex-start; padding:11px 14px; border-radius:10px; font-size:0.86rem; line-height:1.4; margin:0 18px 12px; }
  .insight.warn{background:var(--orange-soft);} .insight.good{background:var(--green-soft);} .insight.info{background:var(--accent-soft);} .insight .ic{font-size:1.05rem;flex-shrink:0;}

  /* inventory table */
  table.mc-table{width:100%;border-collapse:collapse;font-size:0.86rem;}
  .mc-table th{text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--light);font-weight:800;padding:12px 16px;border-bottom:1px solid var(--border);}
  .mc-table td{padding:12px 16px;border-bottom:1px solid var(--border);} .mc-table tr:last-child td{border-bottom:none;}
  .mc-table td.num{font-variant-numeric:tabular-nums;font-weight:700;} .cert-name{font-weight:800;}
  .pill{display:inline-block;padding:2px 9px;border-radius:999px;font-size:0.74rem;font-weight:800;}
  .pill-red{background:var(--red-soft);color:var(--red);} .pill-orange{background:var(--orange-soft);color:var(--orange);} .pill-gray{background:#eef0f4;color:var(--light);}

  /* coverage heatmap */
  .heat { padding:16px 18px; }
  .heat-row{display:grid;grid-template-columns:120px 1fr;align-items:center;gap:12px;margin-bottom:10px;}
  .heat-cells{display:flex;gap:6px;} .heat-cell{flex:1;height:30px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:0.72rem;font-weight:700;color:#fff;}
  .heat-cap{font-weight:800;font-size:0.8rem;color:var(--text);} .heat-legend{display:flex;gap:14px;font-size:0.72rem;color:var(--muted);margin-top:6px;padding-left:132px;}

  /* funnel — centered: label · bar · value hug together */
  .funnel{display:flex;flex-direction:column;align-items:center;gap:3px;padding:16px 18px 18px;}
  .fn-row{display:flex;align-items:center;justify-content:center;gap:12px;width:100%;}
  .fn-lab{width:112px;text-align:right;font-size:0.82rem;font-weight:700;color:var(--text);flex-shrink:0;}
  .fn-bar{height:38px;border-radius:8px;min-width:50px;flex-shrink:0;}
  .fn-val{width:112px;text-align:left;font-size:0.88rem;font-weight:800;font-variant-numeric:tabular-nums;flex-shrink:0;}
  .fn-conv{font-size:0.72rem;color:var(--light);text-align:center;}

  /* insights list */
  .ins-list{padding:6px 0;} .ins-row{display:flex;gap:12px;align-items:flex-start;padding:12px 16px;border-top:1px solid var(--border);font-size:0.88rem;line-height:1.45;}
  .ins-row:first-child{border-top:none;} .ins-row .ic{font-size:1.05rem;flex-shrink:0;}

  /* activity */
  .feed{padding:4px 0;} .feed-row{display:flex;gap:12px;padding:11px 16px;border-top:1px solid var(--border);} .feed-row:first-child{border-top:none;}
  .feed-ic{width:30px;height:30px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:0.95rem;flex-shrink:0;}
  .feed-main{flex:1;font-size:0.86rem;} .feed-time{font-size:0.74rem;color:var(--light);white-space:nowrap;}

  /* command ribbon */
  .ribbon { position:fixed; bottom:0; left:0; right:0; background:var(--bridge-bg); z-index:55; padding:9px 18px; display:flex; align-items:center; gap:9px; box-shadow:0 -4px 18px rgba(16,16,40,0.18); overflow-x:auto; }
  .ribbon .r-label{font-size:0.68rem;font-weight:800;letter-spacing:1px;color:#7a7a9a;text-transform:uppercase;white-space:nowrap;margin-right:4px;}
  .r-btn{display:flex;align-items:center;gap:7px;padding:7px 13px;background:rgba(255,255,255,0.06);border:1px solid #33334e;border-radius:9px;color:#dcdcec;font-size:0.8rem;font-weight:600;cursor:pointer;white-space:nowrap;transition:all .12s;}
  .r-btn:hover{background:rgba(255,255,255,0.14);border-color:#5a5a82;} .r-btn .rb-badge{font-size:0.66rem;font-weight:800;color:#fff;background:var(--red);border-radius:999px;padding:1px 7px;} .r-btn.primary{background:var(--accent);border-color:var(--accent);color:#fff;}

  /* root + coach avatar */
  .mc-root{ min-height:100vh; }\n  [data-nav]{ cursor:pointer; }
  .coach-avatar{ height:78px; width:auto; image-rendering:pixelated; flex-shrink:0; align-self:flex-end; }
`;

const NS = 'http://www.w3.org/2000/svg';
const TAU = Math.PI * 2;
const CERT_COLORS = ['#4a9eff', '#4ad07e', '#b07be0', '#3fc6d4', '#e8881a'];
// Funnel steps. `value` is a sample default; steps with a real data source are
// overridden (and flagged real) in buildFunnelSteps() below. Sample steps are
// tagged in the UI and never participate in a conversion %.
const FUNNEL_STEPS = [
  { key: 'visitors', label: 'Visitors', value: 8420, color: '#1565c0' },
  { key: 'registered', label: 'Registered', value: 5390, color: '#1d72c9' },
  { key: 'active', label: 'Active learners', value: 3030, color: '#2e9e4f' },
  { key: 'ready', label: 'Exam ready', value: 1180, color: '#e8881a' },
  { key: 'certified', label: 'Certified', value: 412, color: '#7a3bbf' },
];

// Overlay real reads (Registered, Active learners) onto the sample funnel.
function buildFunnelSteps(d) {
  d = d || {};
  return FUNNEL_STEPS.map(function (s) {
    if (s.key === 'visitors' && Number.isFinite(d.visitors)) return { ...s, value: d.visitors, real: true };
    if (s.key === 'registered' && Number.isFinite(d.registered)) return { ...s, value: d.registered, real: true };
    if (s.key === 'active' && Number.isFinite(d.activeLearners)) return { ...s, value: d.activeLearners, real: true };
    if (s.key === 'ready' && Number.isFinite(d.examReady)) return { ...s, value: d.examReady, real: true };
    if (s.key === 'certified' && Number.isFinite(d.certified)) return { ...s, value: d.certified, real: true };
    return { ...s, real: false };
  });
}

function fmt(n) { return Number(n || 0).toLocaleString('en-US'); }
function reportedClass(n) { return n <= 0 ? 'pill-gray' : (n <= 3 ? 'pill-orange' : 'pill-red'); }

/** "3:42 PM" from a ms timestamp; "…" while loading. */
function fmtClock(ms) {
  if (!Number.isFinite(ms)) return '…';
  try { return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); }
  catch (e) { return '…'; }
}

// Y-axis labels for the mini area charts. area()/seriesToPoints scale the
// polyline to the series' own min..max, so the labels must be [max, mid, min]
// of the actual data — not invented round numbers.
function seriesYLabels(vals) {
  if (!Array.isArray(vals) || vals.length < 2) return ['', '', ''];
  var max = Math.max.apply(null, vals), min = Math.min.apply(null, vals);
  return [fmt(max), fmt(Math.round((max + min) / 2)), fmt(min)];
}

// `count` evenly-spaced short date labels ("Jul 12") for a window that ends
// on `end` and spans `days` days.
function dateLabels(end, days, count) {
  var out = [];
  var endMs = end instanceof Date ? end.getTime() : Date.now();
  var startMs = endMs - (Math.max(2, days) - 1) * 86400000;
  for (var i = 0; i < count; i++) {
    var d = new Date(startMs + (endMs - startMs) * (count === 1 ? 1 : i / (count - 1)));
    out.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }
  return out;
}

// ── Operational queue (live rows; zero state = green all-clear) ─────────────

function plural(n, word) { return n === 1 ? word : word + 's'; }

/** "today" / "1 day" / "N days" from a whole-day count. */
function fmtAgeDays(days) {
  if (!Number.isFinite(days) || days < 0) return null;
  if (days === 0) return 'today';
  return days + ' ' + plural(days, 'day');
}

/** Short relative age from an ISO timestamp: "just now" / "3h ago" / "2 days ago". */
function fmtAgo(iso) {
  var t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return null;
  var mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return 'just now';
  var hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  var days = Math.round(hrs / 24);
  return days + ' ' + plural(days, 'day') + ' ago';
}

function queueRow(prio, led, text, meta, act, actClass, nav) {
  return '<div class="q-row"><span class="q-prio">' + (prio || '—') + '</span>'
    + '<span class="q-led led-' + led + '"></span>'
    + '<span class="q-text">' + text
    + (meta ? ' <span class="q-meta">· ' + meta + '</span>' : '')
    + '</span><button class="q-act act-' + actClass + '" data-nav="' + nav + '">' + act + '</button></div>';
}

/**
 * The five Operational-queue rows, driven entirely by loaded data:
 *   1. Learner-reported questions  (dashboardService — count + oldest age)
 *   2. QF items pending adjudication (question_factory_dashboard RPC, mig 082)
 *   3. Domain coverage below target  (coverageService matrix, <60% areas)
 *   4. Latest Question Factory run   (list_pipeline_runs RPC)
 *   5. Telemetry health              (did the telemetry read succeed?)
 * Zero/unknown states render as green all-clear / gray unavailable rows.
 */
function buildQueueRows(d) {
  var rows = [];

  // 1. Learner-reported questions
  if (d.reported > 0) {
    var age = fmtAgeDays(d.reportedOldestDays);
    rows.push(queueRow('P1', 'red',
      '<b>' + fmt(d.reported) + ' learner-reported ' + plural(d.reported, 'question') + '</b> awaiting review',
      age ? 'oldest ' + age : '', 'Review', 'red', 'reported'));
  } else {
    rows.push(queueRow('—', 'green', 'No learner-reported questions — review queue clear', '', 'Review', 'ghost', 'reported'));
  }

  // 2. AI-generated questions pending adjudication
  if (d.adjQueue && d.adjQueue.count > 0) {
    rows.push(queueRow('P2', 'orange',
      '<b>' + fmt(d.adjQueue.count) + ' AI-generated ' + plural(d.adjQueue.count, 'question') + '</b> pending adjudication',
      esc(d.adjQueue.certs.join(', ')), 'Adjudicate', 'orange', 'factory'));
  } else if (d.adjQueue) {
    rows.push(queueRow('—', 'green', 'Question Factory — nothing pending adjudication', '', 'Factory', 'ghost', 'factory'));
  } else {
    rows.push(queueRow('—', 'gray', 'Adjudication queue unavailable', 'check Question Factory', 'Factory', 'ghost', 'factory'));
  }

  // 3. Domain coverage below target (areas under 60% of depth target)
  var lowCerts = [], lowAreas = 0;
  (d.coverage || []).forEach(function (row) {
    var low = (row.cells || []).filter(function (c) { return c.depthPct < 60; }).length;
    if (low > 0) { lowCerts.push(row.displayName || row.examId); lowAreas += low; }
  });
  if (lowAreas > 0) {
    rows.push(queueRow('P3', 'yellow',
      '<b>' + esc(lowCerts.join(' + ')) + ' coverage</b> below target',
      lowAreas + ' ' + plural(lowAreas, 'area') + ' under 60%', 'Generate', 'blue', 'factory'));
  } else if ((d.coverage || []).length > 0) {
    rows.push(queueRow('—', 'green', 'Domain coverage on target across all certs', '', 'Generate', 'ghost', 'factory'));
  } else {
    rows.push(queueRow('—', 'gray', 'Coverage data unavailable', '', 'Generate', 'ghost', 'factory'));
  }

  // 4. Latest Question Factory run
  var lr = d.lastRun;
  if (lr === undefined) {
    rows.push(queueRow('—', 'gray', 'Question Factory run status loading…', '', 'View runs', 'ghost', 'factory'));
  } else if (lr === null) {
    rows.push(queueRow('—', 'gray', 'No Question Factory runs yet', '', 'Generate', 'ghost', 'factory'));
  } else {
    var when = fmtAgo(lr.createdAt);
    var counts = fmt(lr.generated) + ' generated, ' + fmt(lr.published) + ' published'
      + (lr.rejected > 0 ? ', ' + fmt(lr.rejected) + ' rejected' : '');
    if (lr.status === 'failed') {
      rows.push(queueRow('P2', 'red', 'Last generation run <b>failed</b>',
        (when ? when + ' · ' : '') + counts, 'View run', 'red', 'factory'));
    } else if (lr.status === 'running' || lr.status === 'queued') {
      rows.push(queueRow('—', 'orange', 'Generation run in progress',
        counts + ' so far', 'View run', 'ghost', 'factory'));
    } else {
      rows.push(queueRow('—', 'green',
        'Last generation run ' + esc(lr.status || 'completed'),
        counts + (when ? ' · ' + when : ''), 'View run', 'ghost', 'factory'));
    }
  }

  // 5. Telemetry health
  if (d.telemetryOk) {
    rows.push(queueRow('—', 'green', 'Telemetry services healthy', '', 'Telemetry', 'ghost', 'telemetry'));
  } else {
    rows.push(queueRow('P2', 'red', 'Telemetry reads failing', 'active-user metrics may be stale', 'Telemetry', 'red', 'telemetry'));
  }

  return rows.join('\n    ');
}
function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

// Build a [x,y] point list (viewBox 0..300 x, 6..76 y inverted) from a numeric series.
function seriesToPoints(vals) {
  if (!Array.isArray(vals) || vals.length < 2) return null;
  var n = vals.length, max = Math.max.apply(null, vals), min = Math.min.apply(null, vals);
  var span = (max - min) || 1;
  return vals.map(function (v, i) {
    return [Math.round(i / (n - 1) * 300), Math.round(76 - ((v - min) / span) * 70)];
  });
}


function heatRows(d) {
  var rows = (d && d.coverage) || [];
  if (!rows.length) {
    return '<div style="padding:14px 2px;color:var(--light);font-size:0.8rem">Loading coverage…</div>';
  }
  var cols = rows.reduce(function (m, r) { return Math.max(m, (r.cells || []).length); }, 0) || 1;
  return rows.map(function (row) {
    var cells = (row.cells || []).map(function (c) {
      var tip = esc(c.category) + ': ' + c.active + ' active / ' + c.target + ' target (' + c.depthPct + '%)' + (c.gap > 0 ? ' \u2014 ' + c.gap + ' to go' : '');
      return '<div class="heat-cell" style="background:' + c.colorVar + '" title="' + tip + '">' + esc(c.label) + ' ' + c.depthPct + '</div>';
    }).join('');
    var summary = row.gapTotal > 0
      ? '<span style="font-size:0.72rem;text-align:right;line-height:1.3"><b>+' + row.gapTotal + '</b> to target<br><span style="color:' + (row.etaUnknown ? 'var(--red)' : 'var(--muted)') + '">' + esc(row.etaText) + '</span></span>'
      : '<span style="font-size:0.72rem;text-align:right;color:var(--green);font-weight:700">\u2713 Fully stocked</span>';
    return '<div class="heat-row" data-nav="dashboard" data-cert="' + esc(row.examId) + '" style="grid-template-columns:120px 1fr 150px">'
      + '<span class="heat-cap">' + esc(row.displayName) + '</span>'
      + '<div class="heat-cells" style="display:grid;grid-template-columns:repeat(' + cols + ',1fr);gap:6px">' + cells + '</div>'
      + summary
      + '</div>';
  }).join('');
}

function certLegend(d) {
  var spans = (d.certMix || []).map(function (s) {
    return '<span><i style="background:' + s.color + '"></i>' + esc(s.label) + '<b>' + s.pct + '%</b></span>';
  }).join('');
  return '<div class="c-legend">' + spans + '</div>';
}

// ── SVG drawing (idempotent) ──
function polar(cx, cy, r, deg) { var a = (deg - 90) * Math.PI / 180; return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; }
function arcPath(cx, cy, r, s, e) { var p = polar(cx, cy, r, e), q = polar(cx, cy, r, s), l = (e - s) <= 180 ? 0 : 1; return ['M', p[0], p[1], 'A', r, r, 0, l, 0, q[0], q[1]].join(' '); }
function pathEl(d, stroke, w) { var p = document.createElementNS(NS, 'path'); p.setAttribute('d', d); p.setAttribute('fill', 'none'); p.setAttribute('stroke', stroke); p.setAttribute('stroke-width', w); p.setAttribute('stroke-linecap', 'round'); return p; }
function gauge(el, value, max, color, size, w) {
  if (!el) return; var old = el.querySelector('svg'); if (old) old.remove();
  var cx = size / 2, cy = size / 2, r = size / 2 - (w / 2 + 4), start = -135, span = 270, frac = Math.max(0, Math.min(1, value / max));
  var svg = document.createElementNS(NS, 'svg'); svg.setAttribute('viewBox', '0 0 ' + size + ' ' + size); svg.setAttribute('width', size); svg.setAttribute('height', size);
  svg.style.position = 'absolute'; svg.style.inset = '0';
  svg.appendChild(pathEl(arcPath(cx, cy, r, start, start + span), 'rgba(255,255,255,0.12)', w));
  svg.appendChild(pathEl(arcPath(cx, cy, r, start, start + span * frac), color, w));
  el.insertBefore(svg, el.firstChild);
}
function ring(el, frac, color, size, w) {
  if (!el) return; var old = el.querySelector('svg'); if (old) old.remove();
  var cx = size / 2, cy = size / 2, r = size / 2 - (w / 2 + 2), C = TAU * r;
  var svg = document.createElementNS(NS, 'svg'); svg.setAttribute('viewBox', '0 0 ' + size + ' ' + size); svg.setAttribute('width', size); svg.setAttribute('height', size);
  svg.style.position = 'absolute'; svg.style.inset = '0';
  var track = document.createElementNS(NS, 'circle'); track.setAttribute('cx', cx); track.setAttribute('cy', cy); track.setAttribute('r', r); track.setAttribute('fill', 'none'); track.setAttribute('stroke', 'rgba(255,255,255,0.10)'); track.setAttribute('stroke-width', w);
  var arc = document.createElementNS(NS, 'circle'); arc.setAttribute('cx', cx); arc.setAttribute('cy', cy); arc.setAttribute('r', r); arc.setAttribute('fill', 'none'); arc.setAttribute('stroke', color); arc.setAttribute('stroke-width', w); arc.setAttribute('stroke-linecap', 'round'); arc.setAttribute('stroke-dasharray', (frac * C) + ' ' + C); arc.setAttribute('transform', 'rotate(-90 ' + cx + ' ' + cy + ')');
  svg.appendChild(track); svg.appendChild(arc); el.insertBefore(svg, el.firstChild);
}
function donut(svg, segs) {
  if (!svg) return; svg.innerHTML = '';
  var cx = 75, cy = 75, r = 52, w = 22, C = TAU * r, off = 0, total = 0;
  segs.forEach(function (s) { total += s.value; }); if (total <= 0) total = 1;
  segs.forEach(function (s) {
    var len = (s.value / total) * C, c = document.createElementNS(NS, 'circle');
    c.setAttribute('cx', cx); c.setAttribute('cy', cy); c.setAttribute('r', r); c.setAttribute('fill', 'none');
    c.setAttribute('stroke', s.color); c.setAttribute('stroke-width', w); c.setAttribute('stroke-dasharray', len + ' ' + (C - len));
    c.setAttribute('stroke-dashoffset', -off); c.setAttribute('transform', 'rotate(-90 ' + cx + ' ' + cy + ')'); svg.appendChild(c); off += len;
  });
}
function area(svg, pts, color, gid) {
  if (!svg || !pts) return; svg.innerHTML = '';
  var baseY = 80;
  var defs = document.createElementNS(NS, 'defs');
  var lg = document.createElementNS(NS, 'linearGradient'); lg.setAttribute('id', gid); lg.setAttribute('x1', '0'); lg.setAttribute('y1', '0'); lg.setAttribute('x2', '0'); lg.setAttribute('y2', '1');
  var s0 = document.createElementNS(NS, 'stop'); s0.setAttribute('offset', '0'); s0.setAttribute('stop-color', color); s0.setAttribute('stop-opacity', '0.5');
  var s1 = document.createElementNS(NS, 'stop'); s1.setAttribute('offset', '1'); s1.setAttribute('stop-color', color); s1.setAttribute('stop-opacity', '0');
  lg.appendChild(s0); lg.appendChild(s1); defs.appendChild(lg); svg.appendChild(defs);
  var line = pts.map(function (p) { return p[0] + ',' + p[1]; }).join(' ');
  var areaPts = pts[0][0] + ',' + baseY + ' ' + line + ' ' + pts[pts.length - 1][0] + ',' + baseY;
  var poly = document.createElementNS(NS, 'polygon'); poly.setAttribute('points', areaPts); poly.setAttribute('fill', 'url(#' + gid + ')'); svg.appendChild(poly);
  [8, 44, 78].forEach(function (gy) { var gl = document.createElementNS(NS, 'line'); gl.setAttribute('x1', 0); gl.setAttribute('x2', 300); gl.setAttribute('y1', gy); gl.setAttribute('y2', gy); gl.setAttribute('stroke', 'rgba(255,255,255,0.13)'); gl.setAttribute('stroke-width', '1'); gl.setAttribute('stroke-dasharray', '4 4'); gl.setAttribute('vector-effect', 'non-scaling-stroke'); svg.appendChild(gl); });
  var pl = document.createElementNS(NS, 'polyline'); pl.setAttribute('points', line); pl.setAttribute('fill', 'none'); pl.setAttribute('stroke', color); pl.setAttribute('stroke-width', '2.4'); pl.setAttribute('stroke-linejoin', 'round'); pl.setAttribute('vector-effect', 'non-scaling-stroke'); svg.appendChild(pl);
  var last = pts[pts.length - 1], dot = document.createElementNS(NS, 'circle'); dot.setAttribute('cx', last[0]); dot.setAttribute('cy', last[1]); dot.setAttribute('r', '3.4'); dot.setAttribute('fill', '#fff'); dot.setAttribute('stroke', color); dot.setAttribute('stroke-width', '2'); dot.setAttribute('vector-effect', 'non-scaling-stroke'); svg.appendChild(dot);
}
function funnel(el, steps) {
  if (!el) return; el.innerHTML = '';
  // Scale bar widths to the largest value so a real (possibly small) step
  // doesn't distort against the sample steps.
  var maxW = 300, total = Math.max.apply(null, steps.map(function (s) { return s.value || 0; })) || 1;
  steps.forEach(function (s, i) {
    var row = document.createElement('div'); row.className = 'fn-row';
    var lab = document.createElement('div'); lab.className = 'fn-lab'; lab.textContent = s.label;
    // Width blends a fixed positional taper (so it always reads as a funnel)
    // with the step's share of the max value (so bigger steps still look bigger).
    var n = steps.length;
    var posFrac = n > 1 ? 1 - 0.80 * (i / (n - 1)) : 1;
    var valFrac = total > 0 ? (s.value / total) : 0;
    var frac = Math.max(0.14, 0.55 * posFrac + 0.45 * valFrac);
    var bar = document.createElement('div'); bar.className = 'fn-bar'; bar.style.width = (frac * maxW) + 'px'; bar.style.background = s.color;
    if (!s.real) bar.style.opacity = '0.5';
    var val = document.createElement('div'); val.className = 'fn-val'; val.textContent = s.value.toLocaleString();
    if (!s.real) {
      var tag = document.createElement('span');
      tag.textContent = ' sample';
      tag.style.cssText = 'font-size:0.62rem;font-weight:700;letter-spacing:0.4px;color:#9aa3b2;text-transform:uppercase;margin-left:6px;';
      val.appendChild(tag);
    }
    row.appendChild(lab); row.appendChild(bar); row.appendChild(val); el.appendChild(row);
    if (i < steps.length - 1) {
      var next = steps[i + 1];
      var c = document.createElement('div'); c.className = 'fn-conv';
      if (s.real && next.real && s.value > 0 && next.value <= s.value) {
        c.textContent = '▼ ' + Math.round(next.value / s.value * 100) + '%';
      } else {
        // Either a sample step, or two real steps measured on different bases
        // (e.g. 30-day Visitors vs all-time Registered) — a % would mislead.
        c.textContent = '▼';
        c.style.opacity = '0.45';
      }
      el.appendChild(c);
    }
  });
}
function drawAll(root, d) {
  if (!root) return;
  var h = (d && d.health) || {};
  var hColor = h.band === 'ok' ? '#43d160' : h.band === 'warn' ? '#e8881a' : h.band === 'bad' ? '#ff5b5b' : 'rgba(255,255,255,0.25)';
  gauge(root.querySelector('#healthGauge'), Number.isFinite(h.score) ? h.score : 0, 100, hColor, 186, 13);
  var rAvg = d.readiness && Number.isFinite(d.readiness.avg) ? d.readiness.avg : 0;
  gauge(root.querySelector('#readyGauge'), rAvg, 100, '#4a9eff', 132, 11);
  donut(root.querySelector('#certDonut'), (d.certMix || []).map(function (s) { return { value: s.pct, color: s.color }; }));
  funnel(root.querySelector('#funnel'), buildFunnelSteps(d));
  ring(root.querySelector('#liveRing'), Number.isFinite(d.liveFrac) ? d.liveFrac : 0, '#43d160', 62, 6);
  area(root.querySelector('#chartDau'), d.dau.points, '#4a9eff', 'gDau');
  area(root.querySelector('#chartMau'), d.mau.points, '#7a3bbf', 'gMau');
}

// ── Placeholder data (defaults; overridden by real reads where available) ──
const PLACEHOLDER = {
  coverage: [],
  live: '—', liveLabel: 'ACTIVE TODAY', liveSub: 'loading live activity…',
  liveFrac: null,             // ring fill: today's actives / 30d peak (null = loading)
  reported: 0,
  loadedAt: null,             // ms timestamp of the last successful loadData()
  // Health starts "unknown" (score null) so the tile shows a syncing state
  // instead of an invented score; loadData() overwrites it with real telemetry.
  health: { score: null, band: 'unknown', errors: 0, errorClusters: 0, slow: 0, failedLoads: 0 },
  // Cross-learner aggregates (admin_learner_health RPC): null = loading/unavailable.
  readiness: null,            // { avg, learners }
  lh: null,                   // raw RPC payload (readiness_dist, domains, low_success, never_served)
  domainSignals: null,        // { examName, strongest, weakest } derived from lh.domains
  qf: undefined,              // Question Factory pipeline stats; undefined = loading, null = unavailable
  qfWeek: null,               // { generated, published, rejected } trailing 7 days
  feed: null,                 // recent-activity items; null = loading, [] = none
  // Operational queue (all replaced by real reads in loadData; see buildQueueRows)
  reportedOldestDays: null,   // days since the oldest still-reported question
  adjQueue: null,             // { count, certs: [displayName] } | null = unavailable
  lastRun: undefined,         // undefined = loading, null = no runs yet, else run summary
  telemetryOk: true,
  bank: [
    { examId: 'pmp', name: 'PMP®', published: '1,284', draft: '42', reported: 9, reportedClass: 'pill-red', avgScore: '71%', avgTime: '68s' },
    { examId: 'pmi-acp', name: 'PMI-ACP®', published: '612', draft: '28', reported: 3, reportedClass: 'pill-orange', avgScore: '74%', avgTime: '59s' },
    { examId: 'pmi-pmocp', name: 'PMI-PMOCP™', published: '318', draft: '61', reported: 0, reportedClass: 'pill-gray', avgScore: '63%', avgScoreClass: 'pill-orange', avgTime: '72s' },
    { examId: 'cpmai', name: 'CPMAI™', published: '274', draft: '12', reported: 0, reportedClass: 'pill-gray', avgScore: '78%', avgTime: '55s' },
  ],
  // Empty until real usage data loads (donut + legend render nothing while empty).
  certMix: [],
  // Charts start flat + unlabeled ("loading" state); real series overwrite them.
  dau: { value: '—', trend: '', trendPct: null, vs: 'loading…', points: [[0, 41], [300, 41]], yLabels: ['', '', ''], xLabels: [] },
  mau: { value: '—', trend: '', trendPct: null, vs: 'loading…', points: [[0, 41], [300, 41]], yLabels: ['', '', ''], xLabels: [] },
};

/**
 * Derives the Mission Control health dial from a telemetry_technical_health
 * payload. Pure, so the calibration can be reasoned about and tested without
 * standing up the whole admin screen.
 *
 * @param {object} tech telemetry_technical_health(p_from, p_to) result
 * @returns {{score:number, band:string, errors:number, errorClusters:number,
 *            slow:number, failedLoads:number}}
 */
export function computeHealthScore(tech) {
  var t = tech || {};
  var errs = Number(t.errors) || 0;
  var slow = Number(t.slow_events) || 0;
  var fails = Number(t.failed_loads) || 0;
  // Pre-098 databases don't return error_clusters. Fall back to the raw count
  // so a staging/prod migration lag can never render the dial as a perfect 100
  // — it degrades to the old, stricter behaviour instead.
  var clusters = t.error_clusters == null ? errs : (Number(t.error_clusters) || 0);
  var score = Math.max(0, 100
    - Math.min(40, clusters * 6)
    - Math.min(15, Math.round(slow / 60))
    - Math.min(15, 2 * Math.max(0, fails - 5)));
  return {
    score: score,
    band: score >= 90 ? 'ok' : (score >= 70 ? 'warn' : 'bad'),
    errors: errs, errorClusters: clusters, slow: slow, failedLoads: fails,
  };
}

// ── Real-data loader (graceful fallback; never throws) ──
// opts.light: skip loadDashboardData. Since migration 099 that call reads the
// bounded questions_admin_summary projection (~2.2MB across all four certs)
// rather than the FULL banks (~18.3MB), so a full pass is no longer expensive
// — but the 5-minute auto-refresh still runs light, because an open Mission
// Control tab re-reading every bank on a timer was inflating the slow-query
// drill-down with the admin's own traffic. Dash-derived fields (reported, bank,
// certMix, coverage, feed) simply keep their last-loaded values on a light pass.

async function loadData(opts) {
  var light = !!(opts && opts.light);
  var today = new Date();
  var from = new Date(); from.setDate(today.getDate() - 29);
  var res = await Promise.allSettled([light ? Promise.resolve(null) : loadDashboardData(), fetchActiveUsers(today), fetchDailyActive(from, today), fetchActiveLearners(from, today), fetchRegisteredUsersCount(), fetchExamReadyLearners(today), fetchCertifiedLearners(today), listPipelineRuns({ limit: 1000 }), getDashboard(), fetchTechnicalHealth(from, today), fetchLearnerHealth(from, today)]);
  var dash = res[0].status === 'fulfilled' ? res[0].value : null;
  var au = res[1].status === 'fulfilled' ? res[1].value : null;
  var dailyRows = res[2].status === 'fulfilled' ? res[2].value : [];
  var activeLearners = res[3].status === 'fulfilled' ? res[3].value : null;
  var registered = res[4].status === 'fulfilled' ? res[4].value : null;
  var examReady = res[5].status === 'fulfilled' ? res[5].value : null;
  var certified = res[6].status === 'fulfilled' ? res[6].value : null;
  var runs = res[7].status === 'fulfilled' ? res[7].value : null;
  var qfDash = res[8].status === 'fulfilled' ? res[8].value : null;
  var tech = res[9].status === 'fulfilled' ? res[9].value : null;
  var lh = res[10].status === 'fulfilled' ? res[10].value : null;
  var tput = {};
  try { tput = aggregateThroughput(runs || []); } catch (e) { /* keep {} */ }
  var ov = {};
  ov.loadedAt = Date.now();

  // ── Platform health (real) ──
  // Same trailing-30-day window and source (telemetry_technical_health) as the
  // Telemetry screen's default view, so the dial, the alert line, and the
  // error count the admin sees after clicking through always agree.
  // Score: start at 100, subtract per-signal penalties. Calibrated 2026-07-26
  // against real prod volumes (the v1 raw-count caps saturated instantly —
  // 429 routine slow reads pegged −20 forever); error term recalibrated
  // 2026-09-15 (migration 098):
  //   • errors: −6 per distinct (message × actor) CLUSTER, cap 40.
  //     v2 charged −2 per raw event, so one actor repeating one message read
  //     as many independent defects — on 2026-09-14 a single Safari actor's
  //     browser extension fired 17 times and took 34 points off a PLATFORM
  //     health dial. A cluster is one problem however often it repeats; a real
  //     regression reaching 7 learners is 7 clusters and still saturates the
  //     cap. `errors` (raw) is kept for display so the dial and the Telemetry
  //     drill-down show the same number.
  //   • slow reads: rate-based — ≈1pt per 2 slow reads/day over the 30-day
  //     window (slow/60), cap 15. Judges the trend, not the raw pile.
  //   • failed loads: first 5 free (post-deploy stale-chunk reloads are
  //     routine for a site that ships often), −2 each beyond, cap 15.
  // Bands: ≥90 healthy · 70–89 monitor · <70 degraded.
  if (tech) ov.health = computeHealthScore(tech);

  // ── Cross-learner readiness + domain signals (admin_learner_health, mig 084) ──
  if (lh) {
    ov.lh = lh;
    if (lh.avg_pct != null && Number.isFinite(Number(lh.avg_pct))) {
      ov.readiness = { avg: Math.round(Number(lh.avg_pct)), learners: Number(lh.learners) || 0 };
    }
    try {
      // Domain signals: strongest + weakest category of the highest-volume
      // exam (min 20 attempts per category so one lucky answer can't headline).
      // (byExamLh — `byExam` is taken by the adjudication-queue block below.)
      var byExamLh = {};
      (lh.domains || []).forEach(function (r) {
        if (!byExamLh[r.exam_id]) byExamLh[r.exam_id] = { total: 0, cats: [] };
        byExamLh[r.exam_id].total += Number(r.attempts) || 0;
        byExamLh[r.exam_id].cats.push(r);
      });
      var topExam = Object.keys(byExamLh).sort(function (a, b) { return byExamLh[b].total - byExamLh[a].total; })[0];
      if (topExam) {
        var cats = byExamLh[topExam].cats.filter(function (c) { return (Number(c.attempts) || 0) >= 20; });
        if (cats.length >= 2) {
          cats.sort(function (a, b) { return Number(b.correct_pct) - Number(a.correct_pct); });
          var cfgT = getExamConfig(topExam);
          ov.domainSignals = {
            examName: (cfgT && cfgT.displayName) || topExam,
            strongest: cats[0],
            weakest: cats[cats.length - 1],
          };
        }
      }
    } catch (e) { /* leave domainSignals null */ }
  }

  // ── Question Factory pipeline (question_factory_dashboard RPC + run sums) ──
  ov.qf = null; // RPC failed → "unavailable" (placeholder `undefined` = loading)
  if (qfDash) {
    ov.qf = {
      generatedToday: Number(qfDash.generated_today) || 0,
      publishedToday: Number(qfDash.published_today) || 0,
      pendingDecision: Number.isFinite(qfDash.pending_adjudication) ? Number(qfDash.pending_adjudication) : null,
      staged: Number(qfDash.pending_review) || 0,
      dupToday: Number(qfDash.duplicates_rejected_today) || 0,
      avgValidation: qfDash.avg_validation_score != null ? Number(qfDash.avg_validation_score) : null,
      avgConsensus: qfDash.avg_consensus_score != null ? Number(qfDash.avg_consensus_score) : null,
      rejectedToday: 0, runsToday: 0,
    };
  }
  try {
    var todayKey = today.toDateString();
    var cutoff7 = Date.now() - 7 * 86400000;
    var wk = { generated: 0, published: 0, rejected: 0 };
    (runs || []).forEach(function (r) {
      var t = r && r.created_at ? new Date(r.created_at) : null;
      if (!t || !Number.isFinite(t.getTime())) return;
      if (ov.qf && t.toDateString() === todayKey) {
        ov.qf.rejectedToday += r.rejected_count || 0;
        ov.qf.runsToday += 1;
      }
      if (t.getTime() >= cutoff7) {
        wk.generated += r.generated_count || 0;
        wk.published += r.published_count || 0;
        wk.rejected += r.rejected_count || 0;
      }
    });
    if (Array.isArray(runs)) ov.qfWeek = wk;
  } catch (e) { /* keep qfWeek null */ }

  // ── Operational queue: telemetry health = the telemetry read succeeded ──
  ov.telemetryOk = !!(au && (Number.isFinite(au.dau) || Number.isFinite(au.mau)));

  // ── Operational queue: latest Question Factory run (runs come newest-first) ──
  if (Array.isArray(runs)) {
    var r0 = runs[0];
    ov.lastRun = r0 ? {
      status: r0.status,
      generated: r0.generated_count || 0,
      published: r0.published_count || 0,
      rejected: r0.rejected_count || 0,
      examId: r0.exam_id || null,
      createdAt: r0.created_at || null,
    } : null;
  }

  // ── Operational queue: items awaiting a human publish/reject decision ──
  try {
    if (qfDash && Number.isFinite(qfDash.pending_adjudication)) {
      var byExam = qfDash.pending_adjudication_by_exam || {};
      var adjCerts = Object.keys(byExam).map(function (id) {
        var c = getExamConfig(id); return (c && c.displayName) || id;
      });
      ov.adjQueue = { count: qfDash.pending_adjudication, certs: adjCerts };
    }
  } catch (e) { /* leave adjQueue null (unavailable) */ }

  // Real funnel steps. Active learners = trailing 30d; Registered = cumulative;
  // Exam ready = current state (latest readiness, any cert).
  if (Number.isFinite(activeLearners)) ov.activeLearners = activeLearners;
  if (Number.isFinite(registered)) ov.registered = registered;
  if (Number.isFinite(examReady)) ov.examReady = examReady;
  if (Number.isFinite(certified)) ov.certified = certified;
  // Visitors = unique actors in the trailing 30 days (internal traffic already
  // excluded by the telemetry view) — that's exactly MAU from fetchActiveUsers.
  if (au && Number.isFinite(au.mau)) ov.visitors = au.mau;

  try {
    if (dash && dash.metrics) {
      var certs = getActiveCertIds();
      var bank = [], mix = [], mixTotal = 0;
      certs.forEach(function (id) {
        var m = dash.metrics[id]; if (!m) return;
        var cfg = getExamConfig(id); var name = (cfg && cfg.displayName) || id;
        var rep = (m.byReview && m.byReview.reported) || 0;
        var sm = dash.sessionMetrics && dash.sessionMetrics[id];
        var avgTime = (sm && sm.totalQuestionsAnswered > 0)
          ? Math.round(sm.totalDurationSeconds / sm.totalQuestionsAnswered) + 's' : '—';
        var sc = sm && sm.full && sm.full.avgScorePct != null ? sm.full.avgScorePct
               : (sm && sm.practice && sm.practice.avgScorePct != null ? sm.practice.avgScorePct : null);
        bank.push({
          examId: id,
          name: name,
          published: fmt((m.byStatus && m.byStatus.active) || 0),
          draft: fmt((m.byStatus && m.byStatus.new) || 0),
          reported: rep, reportedClass: reportedClass(rep),
          avgScore: sc != null ? Math.round(sc) + '%' : '—', avgTime: avgTime,
        });
        var weight = sm ? (sm.totalQuestionsAnswered || 0) : 0;
        mix.push({ label: name, weight: weight }); mixTotal += weight;
      });
      if (bank.length) ov.bank = bank;
      if (mixTotal > 0) {
        ov.certMix = mix.map(function (x, i) { return { label: x.label, pct: Math.round(x.weight / mixTotal * 100), color: CERT_COLORS[i % CERT_COLORS.length] }; });
      }
      var totalReported = dash.overall && dash.overall.byReview ? dash.overall.byReview.reported : null;
      if (totalReported == null) totalReported = (dash.metrics ? Object.keys(dash.metrics).reduce(function (a, k) { return a + ((dash.metrics[k].byReview && dash.metrics[k].byReview.reported) || 0); }, 0) : null);
      if (totalReported != null) ov.reported = totalReported;

      // Oldest still-reported question, in whole days. updated_at is the best
      // available proxy for "when it was flagged" (the flag flip touches the row).
      if (dash.questions) {
        var oldest = null;
        Object.keys(dash.questions).forEach(function (k) {
          (dash.questions[k] || []).forEach(function (q) {
            if (q && q.reviewStatus === 'reported' && q.updatedAt) {
              var t = new Date(q.updatedAt).getTime();
              if (Number.isFinite(t) && (oldest == null || t < oldest)) oldest = t;
            }
          });
        });
        ov.reportedOldestDays = oldest == null ? null
          : Math.max(0, Math.floor((Date.now() - oldest) / 86400000));
      }
    }
  } catch (e) { /* keep placeholders */ }

  try {
    var series = toDailyActiveSeries(dailyRows, from, today).map(function (r) { return r.active_users; });
    if (au && Number.isFinite(au.dau)) {
      ov.live = au.dau; ov.liveLabel = 'ACTIVE TODAY'; ov.liveSub = 'learners active in the last 24 hours';
      // Ring fill = today's actives relative to the trailing-30-day peak day.
      var peak = series.length ? Math.max.apply(null, series) : 0;
      ov.liveFrac = peak > 0 ? Math.max(0, Math.min(1, au.dau / peak)) : null;
      var prev = series.length >= 2 ? series[series.length - 2] : null;
      var tr = (prev != null && prev > 0) ? Math.round((au.dau - prev) / prev * 100) : null;
      var dauSlice = series.slice(-14);
      ov.dau = {
        value: fmt(au.dau),
        trend: tr == null ? '—' : (tr >= 0 ? '▲ ' : '▼ ') + Math.abs(tr) + '%',
        trendPct: tr,
        vs: prev != null ? 'vs yesterday (' + fmt(prev) + ')' : 'last 24h',
        points: seriesToPoints(dauSlice) || PLACEHOLDER.dau.points,
        yLabels: seriesYLabels(dauSlice),
        xLabels: dateLabels(today, Math.min(14, dauSlice.length || 14), 4),
      };
    }
    if (au && Number.isFinite(au.mau)) {
      var first = series.length ? series[0] : null, lastv = series.length ? series[series.length - 1] : null;
      var mtr = (first != null && first > 0 && lastv != null) ? Math.round((lastv - first) / first * 100) : null;
      ov.mau = {
        value: fmt(au.mau),
        trend: mtr == null ? '—' : (mtr >= 0 ? '▲ ' : '▼ ') + Math.abs(mtr) + '%',
        trendPct: mtr,
        vs: 'monthly active users',
        points: seriesToPoints(series) || PLACEHOLDER.mau.points,
        yLabels: seriesYLabels(series),
        xLabels: dateLabels(today, 30, 4),
      };
    }
  } catch (e) { /* keep placeholders */ }

  // ── Recent activity feed (real events from three sources, newest first) ──
  // Skipped on light passes: without dash the history rows would vanish.
  try {
    if (!dash) throw new Error('light-pass');
    var feed = [];
    // 1. Question-history entries (imports, edits, reviews, retires).
    ((dash && dash.recentActivity) || []).slice(0, 10).forEach(function (h) {
      var t = h.changedAt ? new Date(h.changedAt).getTime() : NaN;
      if (!Number.isFinite(t)) return;
      var meta = CHANGE_TYPE_META[h.changeType] || { label: 'Changed', icon: '✏️' };
      var cfgH = h.examId ? getExamConfig(h.examId) : null;
      var certTag = cfgH && cfgH.displayName ? ' — <b>' + esc(cfgH.displayName) + '</b>' : '';
      feed.push({ icon: meta.icon, bg: 'var(--accent-soft)', html: esc(meta.label) + ' question' + certTag + (h.changeNote ? ' <span class="q-meta">· ' + esc(h.changeNote) + '</span>' : ''), ts: t });
    });
    // 2. Latest Question Factory runs.
    (runs || []).slice(0, 3).forEach(function (r) {
      var t = r && r.created_at ? new Date(r.created_at).getTime() : NaN;
      if (!Number.isFinite(t)) return;
      var cfgR = r.exam_id ? getExamConfig(r.exam_id) : null;
      var name = (cfgR && cfgR.displayName) || r.exam_id || '';
      var body = r.status === 'failed'
        ? 'Generation run <b>failed</b>' + (name ? ' — ' + esc(name) : '')
        : 'Generation run' + (name ? ' — <b>' + esc(name) + '</b>' : '') + ', ' + fmt(r.generated_count || 0) + ' generated · ' + fmt(r.published_count || 0) + ' published';
      feed.push({ icon: '🏭', bg: r.status === 'failed' ? 'var(--red-soft)' : 'var(--green-soft)', html: body, ts: t });
    });
    // 3. Latest release note (bundled releases.json — updated every Ship it).
    if (Array.isArray(releases) && releases[0] && releases[0].date) {
      var rel = releases[0];
      var relTs = new Date(rel.date + 'T12:00:00').getTime();
      if (Number.isFinite(relTs)) {
        feed.push({ icon: '🚀', bg: '#eef0f4', html: 'Release deployed — <b>' + esc(rel.version || rel.date) + '</b>' + (rel.title ? ' <span class="q-meta">· ' + esc(rel.title) + '</span>' : ''), ts: relTs });
      }
    }
    feed.sort(function (a, b) { return b.ts - a.ts; });
    ov.feed = feed.slice(0, 6);
  } catch (e) { /* keep feed null (loading) */ }

  try {
    if (dash && dash.questions) {
      ov.coverage = computeCoverageMatrix(dash.questions).map(function (row) {
        var rate = tput[row.examId] && tput[row.examId].perDay;
        var eta = formatEta(row.gapTotal, rate);
        return {
          examId: row.examId,
          displayName: row.displayName,
          gapTotal: row.gapTotal,
          etaText: eta.text,
          etaUnknown: eta.etaDays === null,
          cells: row.cells.map(function (c) {
            return {
              category: c.category, label: c.label, depthPct: c.depthPct,
              gap: c.gap, active: c.active, target: c.target,
              colorVar: bandVar(c.depthPct),
            };
          }),
        };
      });
    }
  } catch (e) { /* keep placeholder coverage */ }

  return ov;
}

// ── Mission Control health tile (derived from d.health / d.reported) ──
// Everything the tile shows is computed here from real data — no hardcoded
// score, alert count, or status. While health is unknown (initial load, or
// the telemetry RPC failed) the tile shows an explicit syncing state.
function healthTileBits(d) {
  var h = (d && d.health) || {};
  var known = Number.isFinite(h.score);
  var band = known ? h.band : 'unknown';
  var parts = [];
  if (h.errors) parts.push(fmt(h.errors) + ' ' + plural(h.errors, 'error'));
  if (h.slow) parts.push(fmt(h.slow) + ' slow ' + (h.slow === 1 ? 'query' : 'queries'));
  if (h.failedLoads) parts.push(fmt(h.failedLoads) + ' failed ' + plural(h.failedLoads, 'load'));
  var total = (h.errors || 0) + (h.slow || 0) + (h.failedLoads || 0);
  var alertLine;
  if (!known) alertLine = '<div class="alert-line ok">Checking telemetry…</div>';
  else if (parts.length) alertLine = '<div class="alert-line">⚠ ' + parts.join(' · ') + (total === 1 ? ' needs' : ' need') + ' attention</div>';
  else alertLine = '<div class="alert-line ok">✓ No active alerts · last 30 days</div>';
  var rep = Number(d.reported) || 0;
  var bubble;
  if (rep > 0) {
    bubble = (band === 'ok' ? 'Platform is healthy — and ' : 'Heads up — ')
      + '<b>' + fmt(rep) + ' ' + plural(rep, 'report') + '</b> '
      + (rep === 1 ? 'is' : 'are') + ' waiting. Start there. 👇';
  } else if (parts.length) {
    bubble = 'No reports waiting — but <b>' + parts.join(' · ') + '</b> in telemetry could use a look. ↗';
  } else if (known) {
    bubble = 'All clear! No reports waiting and no active alerts. 🎉';
  } else {
    bubble = 'Syncing live platform data…';
  }
  return {
    scoreText: known ? String(h.score) : '—',
    statusClass: band === 'ok' ? '' : ' ' + band,
    statusLabel: { ok: '🟢 HEALTHY', warn: '🟠 MONITOR', bad: '🔴 DEGRADED', unknown: '⏳ CHECKING…' }[band] || '⏳ CHECKING…',
    alertLine: alertLine,
    bubble: bubble,
  };
}

// ── Trend badge for the DAU/MAU metric cards (derived from the real trend) ──
function growBadge(m) {
  var t = m && m.trendPct;
  if (!Number.isFinite(t)) return '';
  if (t > 2) return '<span class="mc2-grow">↑ GROWING</span>';
  if (t < -2) return '<span class="mc2-grow down">↓ DECLINING</span>';
  return '<span class="mc2-grow flat">→ STEADY</span>';
}

// ── Learner readiness console tile context line ──
function readinessCtx(d) {
  if (!d.readiness) {
    return 'Measuring answer correctness across all learners (trailing 30 days)…';
  }
  var out = 'Avg correctness across <b>' + fmt(d.readiness.learners) + ' ' + plural(d.readiness.learners, 'learner') + '</b>, trailing 30 days.';
  if (d.domainSignals && d.domainSignals.weakest) {
    out += ' Weakest area: <b>' + esc(d.domainSignals.weakest.category) + '</b> (' + Math.round(Number(d.domainSignals.weakest.correct_pct)) + '%).';
  }
  return out;
}

// ── Question Factory throughput section (pipe nodes + metric chips) ──
function factorySection(d) {
  var qf = d.qf;
  if (qf === undefined) {
    return '<div style="padding:22px 18px;color:var(--light);font-size:0.85rem">Loading pipeline data…</div>';
  }
  if (!qf) {
    return '<div style="padding:22px 18px;color:var(--light);font-size:0.85rem">Question Factory data unavailable — open the Factory for details.</div>';
  }
  var dropToPub = qf.generatedToday > qf.publishedToday
    ? '−' + fmt(qf.generatedToday - qf.publishedToday) + ' in review' : 'no drop';
  var nodes =
    '<div class="pipe-stage"><div class="pipe-node"><div class="pn-v">' + fmt(qf.generatedToday) + '</div><div class="pn-l">Generated</div><div class="pn-drop">today</div></div><span class="pipe-arrow">▶</span></div>'
    + '<div class="pipe-stage"><div class="pipe-node"><div class="pn-v" style="color:var(--accent)">' + fmt(qf.publishedToday) + '</div><div class="pn-l">Published</div><div class="pn-drop">today · ' + dropToPub + '</div></div><span class="pipe-arrow">▶</span></div>'
    + '<div class="pipe-stage"><div class="pipe-node"><div class="pn-v" style="color:var(--orange)">' + (qf.pendingDecision == null ? '—' : fmt(qf.pendingDecision)) + '</div><div class="pn-l">Awaiting decision</div><div class="pn-drop">in queue now</div></div><span class="pipe-arrow">▶</span></div>'
    + '<div class="pipe-stage"><div class="pipe-node"><div class="pn-v" style="color:var(--green)">' + fmt(qf.staged) + '</div><div class="pn-l">Staged</div><div class="pn-drop">ready to release</div></div></div>';
  var chips =
    '<div class="pm-chip"><div class="v">' + fmt(qf.runsToday) + '</div><div class="l">' + plural(qf.runsToday, 'Run') + ' today</div></div>'
    + '<div class="pm-chip"><div class="v">' + fmt(qf.rejectedToday) + '</div><div class="l">Rejected today</div></div>'
    + '<div class="pm-chip"><div class="v">' + fmt(qf.dupToday) + '</div><div class="l">Duplicate rejects</div></div>'
    + '<div class="pm-chip"><div class="v">' + (qf.avgValidation == null ? '—' : qf.avgValidation.toFixed(1)) + '</div><div class="l">Avg validation</div></div>'
    + '<div class="pm-chip"><div class="v">' + (qf.avgConsensus == null ? '—' : qf.avgConsensus.toFixed(1)) + '</div><div class="l">Avg consensus</div></div>';
  return '<div class="pipe" data-nav="factory">' + nodes + '</div>'
    + '<div class="pipe-metrics" data-nav="factory">' + chips + '</div>';
}

// ── Readiness distribution card (admin_learner_health readiness_dist) ──
var RD_BANDS = [
  { key: '75-100', label: '75–100%', color: 'var(--green)' },
  { key: '50-75', label: '50–75%', color: 'var(--accent)' },
  { key: '25-50', label: '25–50%', color: 'var(--orange)' },
  { key: '0-25', label: '0–25%', color: 'var(--red)' },
];
function readinessDistRows(d) {
  var lh = d.lh;
  if (!lh || !Array.isArray(lh.readiness_dist)) {
    return '<div style="padding:4px 2px;color:var(--light);font-size:0.82rem">Loading learner data…</div>';
  }
  var byBand = {};
  lh.readiness_dist.forEach(function (r) { byBand[r.band] = Number(r.n) || 0; });
  var totalN = Number(lh.learners) || 0;
  if (totalN <= 0) {
    return '<div style="padding:4px 2px;color:var(--light);font-size:0.82rem">No learner attempts recorded in the last 30 days.</div>';
  }
  return RD_BANDS.map(function (b) {
    var n = byBand[b.key] || 0;
    var pct = Math.round(n / totalN * 100);
    return '<div class="rd-bar-row"><span class="rd-lab">' + b.label + '</span>'
      + '<div class="rd-track"><div class="rd-fill" style="width:' + pct + '%;background:' + b.color + '"></div></div>'
      + '<span class="rd-n">' + pct + '% · ' + fmt(n) + '</span></div>';
  }).join('');
}

// ── Domain signals card (strongest / weakest category, top-volume exam) ──
function domainSignalRows(d) {
  if (!d.lh) {
    return '<div class="insight info"><span class="ic">⏳</span><div>Loading domain performance…</div></div>';
  }
  var s = d.domainSignals;
  if (!s) {
    return '<div class="insight info"><span class="ic">📊</span><div>Not enough attempt volume yet for per-domain signals (min 20 attempts per domain).</div></div>';
  }
  return '<div class="insight good"><span class="ic">✅</span><div><b>' + esc(s.strongest.category) + '</b> is the strongest domain (' + Math.round(Number(s.strongest.correct_pct)) + '% avg over ' + fmt(s.strongest.attempts) + ' attempts).</div></div>'
    + '<div class="insight warn"><span class="ic">⚠</span><div><b>' + esc(s.weakest.category) + '</b> is weakest (' + Math.round(Number(s.weakest.correct_pct)) + '% avg). Consider a featured drill or targeted generation.</div></div>';
}

// ── Recent activity feed rows ──
function feedRows(d) {
  if (!Array.isArray(d.feed)) {
    return '<div class="feed-row"><div class="feed-ic" style="background:#eef0f4">⏳</div><div class="feed-main" style="color:var(--light)">Loading activity…</div></div>';
  }
  if (!d.feed.length) {
    return '<div class="feed-row"><div class="feed-ic" style="background:#eef0f4">🌙</div><div class="feed-main" style="color:var(--light)">No recent operational events.</div></div>';
  }
  return d.feed.map(function (f) {
    return '<div class="feed-row"><div class="feed-ic" style="background:' + f.bg + '">' + f.icon + '</div>'
      + '<div class="feed-main">' + f.html + '</div>'
      + '<span class="feed-time">' + (fmtAgo(new Date(f.ts).toISOString()) || '') + '</span></div>';
  }).join('');
}

// ── "What matters this week" — synthesized from the loaded real data ──
function insightRows(d) {
  var rows = [];
  if (d.readiness) {
    rows.push({ ic: '✅', html: '<b>' + fmt(d.readiness.learners) + ' ' + plural(d.readiness.learners, 'learner') + '</b> answered questions in the last 30 days — avg correctness <b>' + d.readiness.avg + '%</b>.' });
  }
  if (d.qfWeek && d.qfWeek.generated > 0) {
    var acc = Math.round(d.qfWeek.published / d.qfWeek.generated * 100);
    rows.push({ ic: '🔥', html: '<b>' + acc + '%</b> of the ' + fmt(d.qfWeek.generated) + ' AI-generated questions this week reached publication.' });
  }
  if (d.qfWeek && d.qfWeek.published > 0) {
    rows.push({ ic: '🎉', html: '<b>' + fmt(d.qfWeek.published) + ' new ' + plural(d.qfWeek.published, 'question') + '</b> published via the Question Factory in the last 7 days.' });
  }
  // Worst under-target coverage cell across all certs.
  try {
    var worst = null, worstExam = null;
    (d.coverage || []).forEach(function (row) {
      (row.cells || []).forEach(function (c) {
        if (c.depthPct < 100 && c.gap > 0 && (!worst || c.depthPct < worst.depthPct)) { worst = c; worstExam = row.displayName; }
      });
    });
    if (worst && worst.depthPct < 80) {
      rows.push({ ic: '⚠', html: esc(worstExam) + ' <b>' + esc(worst.category) + '</b> inventory is below target (' + worst.depthPct + '% · ' + fmt(worst.gap) + ' to go).' });
    }
  } catch (e) { /* skip coverage insight */ }
  if (d.lh && Number(d.lh.low_success) > 0) {
    rows.push({ ic: '📉', html: '<b>' + fmt(d.lh.low_success) + ' ' + plural(Number(d.lh.low_success), 'question') + '</b> show unusually low success (&lt;30%, ≥10 attempts, 30d).' });
  }
  if (d.lh && Number(d.lh.never_served) > 0) {
    rows.push({ ic: '🕳️', html: '<b>' + fmt(d.lh.never_served) + ' published ' + plural(Number(d.lh.never_served), 'question') + '</b> have never been served to a learner.' });
  }
  if (!rows.length) {
    var loading = !d.lh && !d.qfWeek;
    return '<div class="ins-row"><span class="ic">' + (loading ? '⏳' : '🌱') + '</span><div style="color:var(--light)">'
      + (loading ? 'Synthesizing observations…' : 'Insights will appear as platform activity accumulates.') + '</div></div>';
  }
  return rows.map(function (r) {
    return '<div class="ins-row"><span class="ic">' + r.ic + '</span><div>' + r.html + '</div></div>';
  }).join('');
}

function buildVisualHTML(d) {
  const hb = healthTileBits(d);
  return `<!-- ===== PLATFORM PULSE BANNER ===== -->
  <div class="pulse-banner" data-nav="telemetry">
    <div class="pb-brand">
      <span class="pulse-ico"><svg width="24" height="24" viewBox="0 0 24 24"><path d="M3 12h4l3 8 4-16 3 8h4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
      <div><div class="pb-title">PLATFORM PULSE</div><div class="pb-sub">Real-time engagement overview</div></div>
    </div>
    <div class="pb-live">
      <div class="hero-ring" id="liveRing"><div class="lr-ico"><svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg></div></div>
      <div><div class="pb-num">${d.live} <span class="pb-numlab">${d.liveLabel}</span></div><div class="pb-sub">${d.liveSub}</div></div>
    </div>
    <div class="pb-status">
      <span class="live-pill"><span class="ld"></span> LIVE NOW</span>
      <div class="pb-upd">updated ${fmtClock(d.loadedAt)} ⟳</div>
    </div>
  </div>

  <!-- ===== UNIFIED CONSOLE ===== -->
  <div class="console">
    <div class="console-table">
      <div class="tile health-tile click" data-nav="telemetry" title="Open platform telemetry →">
        <span class="open-ind">↗</span>
        <div class="con-title">PM Pixel Mission Control</div>
        <div class="health-wrap" id="healthGauge"><div class="health-center"><div class="hc-v">${hb.scoreText}</div><div class="hc-of">/ 100</div><div class="hc-l">Platform Health</div></div></div>
        <div class="status${hb.statusClass}"><span class="led"></span> ${hb.statusLabel}</div>
        ${hb.alertLine}
        <div class="coach">
          <img class="coach-avatar" src="/themes/pm-pixel/mascot/PM_PIXEL_THUMBSUP.png" alt="" aria-hidden="true" onerror="this.style.display='none'" />
          <div class="bubble">${hb.bubble}</div>
        </div>
      </div>

      <div class="col-stack">
        <div class="tile click" data-nav="telemetry" title="Open learner readiness →">
          <span class="tile-cap">Learner readiness</span><span class="open-ind">↗</span>
          <div class="instr-body">
            <div class="gauge-md" id="readyGauge"><div class="gauge-md-center"><div class="gv">${d.readiness ? d.readiness.avg + '%' : '—'}</div><div class="gl">avg correct · 30d</div></div></div>
            <div class="ctx">${readinessCtx(d)}</div>
          </div>
        </div>
        <div class="tile click" data-nav="telemetry" title="Open certification usage →">
          <span class="tile-cap">Certification mix · today</span><span class="open-ind">↗</span>
          <div class="instr-body">
            <svg width="124" height="124" viewBox="0 0 150 150" id="certDonut" style="flex-shrink:0;"></svg>
            ${certLegend(d)}
          </div>
        </div>
      </div>

      <!-- COL 3: PLATFORM PULSE METRICS (formatted) -->
      <div class="col-stack">
        <div class="tile metric-card click" data-nav="telemetry" title="Open Platform Pulse →">
          <div class="mc2-top"><span class="mc2-ico" style="background:#2f6fd0"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg></span><span class="mc2-title">Daily Active Users</span>${growBadge(d.dau)}</div>
          <div class="mc2-val">${d.dau.value} <span class="tr">${d.dau.trend}</span></div>
          <div class="mc2-vs">${d.dau.vs}</div>
          <div class="mc2-chart"><div class="mc2-yax">${(d.dau.yLabels || []).map(function (y) { return '<span>' + y + '</span>'; }).join('')}</div><div class="mc2-plot"><svg id="chartDau" viewBox="0 0 300 80" preserveAspectRatio="none"></svg></div></div>
          <div class="mc2-axis">${(d.dau.xLabels || []).map(function (x) { return '<span>' + x + '</span>'; }).join('')}</div>
        </div>
        <div class="tile metric-card click" data-nav="telemetry" title="Open Platform Pulse →">
          <div class="mc2-top"><span class="mc2-ico" style="background:#7a3bbf"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M7 2v2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V2h-2v2H9V2H7zm12 7v10H5V9h14z"/></svg></span><span class="mc2-title">Monthly Active Users</span>${growBadge(d.mau)}</div>
          <div class="mc2-val">${d.mau.value} <span class="tr">${d.mau.trend}</span></div>
          <div class="mc2-vs">${d.mau.vs}</div>
          <div class="mc2-chart"><div class="mc2-yax">${(d.mau.yLabels || []).map(function (y) { return '<span>' + y + '</span>'; }).join('')}</div><div class="mc2-plot"><svg id="chartMau" viewBox="0 0 300 80" preserveAspectRatio="none"></svg></div></div>
          <div class="mc2-axis">${(d.mau.xLabels || []).map(function (x) { return '<span>' + x + '</span>'; }).join('')}</div>
        </div>
      </div>
    </div>
  </div>

<style>
  /* Operational queue */
  .queue { padding:6px 6px 8px; }
  .q-row { display:flex; align-items:center; gap:14px; padding:13px 16px; margin:0 8px; border-top:1px solid var(--border); }
  .q-row:first-child { border-top:none; }
  .q-led { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
  .led-red{background:var(--red);box-shadow:0 0 0 4px var(--red-soft);} .led-orange{background:var(--orange);box-shadow:0 0 0 4px var(--orange-soft);}
  .led-yellow{background:var(--yellow);box-shadow:0 0 0 4px #fbf3d0;} .led-green{background:var(--green);box-shadow:0 0 0 4px var(--green-soft);}
  .led-gray{background:#c3c3d1;box-shadow:0 0 0 4px #eef0f4;}
  .q-text{flex:1;font-size:0.92rem;} .q-text b{font-weight:800;} .q-meta{font-size:0.76rem;color:var(--light);}
  .q-act{padding:6px 15px;border-radius:8px;border:none;font-size:0.8rem;font-weight:700;cursor:pointer;color:#fff;}
  .act-red{background:var(--red);} .act-orange{background:var(--orange);} .act-blue{background:var(--accent);} .act-ghost{background:#eef0f4;color:var(--muted);}
  .q-prio{font-size:0.66rem;font-weight:800;letter-spacing:0.5px;color:var(--light);width:30px;text-align:center;}

  /* pipeline */
  .pipe { display:flex; align-items:stretch; padding:20px 18px 8px; flex-wrap:wrap; }
  .pipe-stage { flex:1; min-width:120px; text-align:center; position:relative; }
  .pipe-node { background:#fafbfd; border:2px solid var(--border); border-radius:12px; padding:14px 8px; margin:0 6px; }
  .pipe-node .pn-v { font-size:1.8rem; font-weight:800; line-height:1; } .pipe-node .pn-l { font-size:0.76rem; color:var(--muted); font-weight:700; margin-top:5px; } .pipe-node .pn-drop { font-size:0.68rem; color:var(--light); margin-top:3px; }
  .pipe-arrow { position:absolute; right:-9px; top:50%; transform:translateY(-50%); font-size:1.1rem; color:var(--light); z-index:2; }
  .pipe-metrics { display:grid; grid-template-columns:repeat(5,1fr); gap:10px; padding:8px 18px 18px; }
  .pm-chip { background:#fafbfd; border:1px solid var(--border); border-radius:10px; padding:9px 10px; text-align:center; }
  .pm-chip .v{font-size:1.05rem;font-weight:800;} .pm-chip .l{font-size:0.68rem;color:var(--muted);margin-top:2px;}
  @media(max-width:760px){ .pipe-metrics{grid-template-columns:repeat(2,1fr);} }

  /* readiness distribution + insights */
  .rd-dist { padding:18px; } .rd-bar-row { display:flex; align-items:center; gap:10px; margin-bottom:9px; }
  .rd-lab{width:72px;font-size:0.78rem;color:var(--muted);} .rd-track{flex:1;background:#eef0f4;border-radius:6px;height:14px;overflow:hidden;}
  .rd-fill{height:100%;border-radius:6px;} .rd-n{width:90px;text-align:right;font-size:0.78rem;font-weight:700;}
  .insight { display:flex; gap:10px; align-items:flex-start; padding:11px 14px; border-radius:10px; font-size:0.86rem; line-height:1.4; margin:0 18px 12px; }
  .insight.warn{background:var(--orange-soft);} .insight.good{background:var(--green-soft);} .insight.info{background:var(--accent-soft);} .insight .ic{font-size:1.05rem;flex-shrink:0;}

  /* inventory table */
  table.mc-table{width:100%;border-collapse:collapse;font-size:0.86rem;}
  .mc-table th{text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--light);font-weight:800;padding:12px 16px;border-bottom:1px solid var(--border);}
  .mc-table td{padding:12px 16px;border-bottom:1px solid var(--border);} .mc-table tr:last-child td{border-bottom:none;}
  .mc-table td.num{font-variant-numeric:tabular-nums;font-weight:700;} .cert-name{font-weight:800;}
  .pill{display:inline-block;padding:2px 9px;border-radius:999px;font-size:0.74rem;font-weight:800;}
  .pill-red{background:var(--red-soft);color:var(--red);} .pill-orange{background:var(--orange-soft);color:var(--orange);} .pill-gray{background:#eef0f4;color:var(--light);}

  /* coverage heatmap */
  .heat { padding:16px 18px; }
  .heat-row{display:grid;grid-template-columns:120px 1fr;align-items:center;gap:12px;margin-bottom:10px;}
  .heat-cells{display:flex;gap:6px;} .heat-cell{flex:1;height:30px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:0.72rem;font-weight:700;color:#fff;}
  .heat-cap{font-weight:800;font-size:0.8rem;color:var(--text);} .heat-legend{display:flex;gap:14px;font-size:0.72rem;color:var(--muted);margin-top:6px;padding-left:132px;}

  /* funnel — centered: label · bar · value hug together */
  .funnel{display:flex;flex-direction:column;align-items:center;gap:3px;padding:16px 18px 18px;}
  .fn-row{display:flex;align-items:center;justify-content:center;gap:12px;width:100%;}
  .fn-lab{width:112px;text-align:right;font-size:0.82rem;font-weight:700;color:var(--text);flex-shrink:0;}
  .fn-bar{height:38px;border-radius:8px;min-width:50px;flex-shrink:0;}
  .fn-val{width:112px;text-align:left;font-size:0.88rem;font-weight:800;font-variant-numeric:tabular-nums;flex-shrink:0;}
  .fn-conv{font-size:0.72rem;color:var(--light);text-align:center;}

  /* insights list */
  .ins-list{padding:6px 0;} .ins-row{display:flex;gap:12px;align-items:flex-start;padding:12px 16px;border-top:1px solid var(--border);font-size:0.88rem;line-height:1.45;}
  .ins-row:first-child{border-top:none;} .ins-row .ic{font-size:1.05rem;flex-shrink:0;}

  /* activity */
  .feed{padding:4px 0;} .feed-row{display:flex;gap:12px;padding:11px 16px;border-top:1px solid var(--border);} .feed-row:first-child{border-top:none;}
  .feed-ic{width:30px;height:30px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:0.95rem;flex-shrink:0;}
  .feed-main{flex:1;font-size:0.86rem;} .feed-time{font-size:0.74rem;color:var(--light);white-space:nowrap;}

  /* command ribbon */
  .ribbon { position:fixed; bottom:0; left:0; right:0; background:var(--bridge-bg); z-index:55; padding:9px 18px; display:flex; align-items:center; gap:9px; box-shadow:0 -4px 18px rgba(16,16,40,0.18); overflow-x:auto; }
  .ribbon .r-label{font-size:0.68rem;font-weight:800;letter-spacing:1px;color:#7a7a9a;text-transform:uppercase;white-space:nowrap;margin-right:4px;}
  .r-btn{display:flex;align-items:center;gap:7px;padding:7px 13px;background:rgba(255,255,255,0.06);border:1px solid #33334e;border-radius:9px;color:#dcdcec;font-size:0.8rem;font-weight:600;cursor:pointer;white-space:nowrap;transition:all .12s;}
  .r-btn:hover{background:rgba(255,255,255,0.14);border-color:#5a5a82;} .r-btn .rb-badge{font-size:0.66rem;font-weight:800;color:#fff;background:var(--red);border-radius:999px;padding:1px 7px;} .r-btn.primary{background:var(--accent);border-color:var(--accent);color:#fff;}
</style>

<!-- 1. OPERATIONAL QUEUE -->
<div class="sec">
  <div class="sec-head"><span class="sec-num">1</span><h2 class="sec-title">Operational queue — start here</h2><span class="sec-sub">prioritized by urgency</span></div>
  <div class="card queue">
    ${buildQueueRows(d)}
  </div>
</div>

<!-- 2. QUESTION FACTORY PIPELINE -->
<div class="sec">
  <div class="sec-head"><span class="sec-num">2</span><h2 class="sec-title">AI Question Factory — throughput</h2><span class="sec-sub">today's pipeline flow</span></div>
  <div class="card">
    ${factorySection(d)}
  </div>
</div>

<!-- 3. LEARNER HEALTH (distribution + insights; gauge now in console) -->
<div class="sec">
  <div class="sec-head"><span class="sec-num">3</span><h2 class="sec-title">Learner health detail</h2><span class="sec-sub">readiness distribution &amp; signals</span></div>
  <div class="grid-2">
    <div class="card">
      <div class="card-h"><h3>Readiness distribution</h3><p>${d.lh ? fmt(d.lh.learners) + ' active ' + plural(Number(d.lh.learners) || 0, 'learner') + ' · answer correctness · last 30 days' : 'last 30 days'}</p></div>
      <div class="rd-dist">
        ${readinessDistRows(d)}
      </div>
    </div>
    <div class="card">
      <div class="card-h"><h3>Domain signals</h3><p>${d.domainSignals ? 'across ' + esc(d.domainSignals.examName) + ' learners · last 30 days' : 'last 30 days'}</p></div>
      <div style="padding-top:12px;">
        ${domainSignalRows(d)}
      </div>
    </div>
  </div>
</div>

<!-- 4. DOMAIN COVERAGE -->
<div class="sec">
  <div class="sec-head"><span class="sec-num">4</span><h2 class="sec-title">Domain coverage</h2><span class="sec-sub">active inventory vs blueprint-weighted target</span></div>
  <div class="card">
    <div class="card-h"><h3>Domain coverage heatmap</h3><p>active inventory vs target \u00b7 fill-up ETA from Question Factory</p></div>
    <div class="heat">
      ${heatRows(d)}
      <div class="heat-legend"><span>\ud83d\udfe5 &lt;60</span><span>\ud83d\udfe7 60\u201379</span><span>\ud83d\udfe6 80\u201399</span><span>\ud83d\udfe9 100</span></div>
    </div>
  </div>
</div>

<!-- 5. RECENT ACTIVITY (sparklines now in console) -->
<div class="sec">
  <div class="sec-head"><span class="sec-num">5</span><h2 class="sec-title">Recent activity</h2><span class="sec-sub">operational event log</span></div>
  <div class="card feed">
    ${feedRows(d)}
  </div>
</div>

<!-- 6. LONG-TERM INSIGHTS -->
<div class="sec">
  <div class="sec-head"><span class="sec-num">6</span><h2 class="sec-title">Long-term insights</h2><span class="sec-sub">trailing 30 days</span></div>
  <div class="grid-2">
    <div class="card">
      <div class="card-h"><h3>Certification funnel</h3><p>visitor → certified · all steps live</p></div>
      <div class="funnel" id="funnel" data-nav="telemetry"></div>
    </div>
    <div class="card">
      <div class="card-h"><h3>What matters this week</h3><p>synthesized from live data</p></div>
      <div class="ins-list">
        ${insightRows(d)}
      </div>
    </div>
  </div>
</div>`;
}

// data-nav -> onOpen* callback prop (delegated drill-down from the visual canvas).
const NAV = { telemetry: 'onOpenTelemetry', editor: 'onOpenEditor', reported: 'onOpenReportedQuestions', dashboard: 'onOpenDashboard', factory: 'onOpenQuestionFactory' };

// Quick Actions ribbon — extracted to the shared AdminQuickActions component
// (rendered below) so every admin screen carries the same floating footer.

export default function AdminScreen(props) {
  const { onBack, reportCount = 0 } = props;
  const auth = useContext(AdminGateContext);
  const visualRef = useRef(null);

  const handleNav = (e) => {
    const el = e.target.closest && e.target.closest('[data-nav]');
    if (!el) return;
    const cb = props[NAV[el.getAttribute('data-nav')]];
    if (typeof cb !== 'function') return;
    const cert = el.getAttribute('data-cert');
    const view = el.getAttribute('data-view');
    if (cert || view) cb({ selectedCert: cert || undefined, view: view || undefined });
    else cb();
  };
  const [data, setData] = useState(PLACEHOLDER);

  useEffect(() => {
    let alive = true;
    const refresh = (opts) =>
      loadData(opts).then((ov) => { if (alive && ov && Object.keys(ov).length) setData((p) => ({ ...p, ...ov })); }).catch(() => {});
    refresh();
    // Real auto-refresh, matching the header's "auto-refresh 5m" promise.
    // Light passes skip the four full-bank pulls; see loadData.
    const timer = setInterval(() => refresh({ light: true }), 5 * 60 * 1000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  const html = useMemo(() => buildVisualHTML(data), [data]);

  useEffect(() => { drawAll(visualRef.current, data); }, [data]);


  return (
    <div data-testid="admin-screen" className="mc-root">
      <style>{CSS}</style>

      <div className="mc-top">
        <button data-testid="admin-back-button" className="back" onClick={onBack}>← Back</button>
        <span className="brand">⚙️ Mission Control</span>
        <span className="sp" />
        <span className="meta">⟳ Updated {data.loadedAt ? fmtClock(data.loadedAt) : '…'} · auto-refresh 5m</span>
        {auth && <span className="user">{auth.userEmail}</span>}
        {auth && (
          <button data-testid="admin-signout-button" className="signout" onClick={auth.onSignOut}>Sign out</button>
        )}
      </div>

      <div className="mc-body">
        <div ref={visualRef} className="mc-canvas" onClick={handleNav} dangerouslySetInnerHTML={{ __html: html }} />

        <AdminQuickActions
          activeView={null}
          reportCount={reportCount}
          onAction={(toolId) => {
            const tool = QUICK_ACTION_TOOLS.find((t) => t.id === toolId);
            const cb = tool && tool.cb ? props[tool.cb] : null;
            if (typeof cb === 'function') cb();
          }}
        />
      </div>
    </div>
  );
}
